import { DotsThree } from '@phosphor-icons/react'
import { useCallback, useRef, forwardRef } from 'react'
import { IconButtonV4 } from '../Button/IconButton.v4'
import { Divider } from '../Divider'
import { Popover, usePopover } from '../Popover'
import { Stack } from '../Stack'
import {
  type ActionButtonProps,
  ActionMenu,
  MenuTitle,
} from './ActionButton.v4'
import { popoverCss } from './ActionButton.v4.css'

export interface ActionIconButtonProps extends ActionButtonProps {
  icon?: React.ElementType
}

type ActionIconButtonWithDivider = React.ForwardRefExoticComponent<
  ActionIconButtonProps & React.RefAttributes<HTMLButtonElement>
> & {
  Divider: typeof ActionMenu.Divider
}

export const ActionIconButtonV4: ActionIconButtonWithDivider = forwardRef<
  HTMLButtonElement,
  ActionIconButtonProps
>(function ActionIconButtonV4(
  {
    actions,
    placement = 'bottom',
    disabled,
    title,
    icon = DotsThree,
    ...boxProps
  },
  forwardedRef,
) {
  const ref = useRef<HTMLButtonElement>(null)

  const { triggerProps, popoverProps, setOpen } = usePopover({
    placement,
    triggerRef:
      (typeof forwardedRef === 'function' ? ref : forwardedRef) || ref,
    trigger: 'menu',
  })

  const closeMenu = useCallback(() => {
    setOpen(false)
  }, [setOpen])

  return (
    <>
      <IconButtonV4 icon={icon} {...boxProps} {...triggerProps} />
      <Popover className={popoverCss} {...popoverProps} hideArrow>
        <Stack gap="1x" margin="1x">
          {title && (
            <>
              <MenuTitle>{title}</MenuTitle>
              <Divider variant="secondary" bleedX="1x" />
            </>
          )}
          <ActionMenu actions={actions} closeMenu={closeMenu} />
        </Stack>
      </Popover>
    </>
  )
  // Casting so that we can add Divider to the component
}) as ActionIconButtonWithDivider

ActionIconButtonV4.Divider = ActionMenu.Divider
